"use client";

import posthog from "posthog-js";
import { hasAnalyticsConsent } from "./cookie-consent";

// Fired at the actual button click, not inside the booking hook itself —
// confirmBooking() also re-runs programmatically after a Google sign-in
// round trip, which isn't a new user click and would double-count.
export function trackBookClick(props: {
  activityId: string;
  price: number;
  kind: "slot" | "fixed_session";
}) {
  posthog.capture("book_click", props);
}

// Called from instrumentation-client.ts on cold load (for returning users
// who already consented) and from the consent banner's onFirstConsent/
// onChange callbacks (for the live "Accept" click) — see
// components/cookie-consent-banner.tsx. No-ops until both an analytics
// project key AND user consent are present.
export function initAnalyticsIfConsented(): void {
  if (typeof document === "undefined") return;
  if (!hasAnalyticsConsent(document.cookie)) return;

  const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  if (!key) return;

  posthog.init(key, {
    api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST ?? "https://eu.i.posthog.com",
    // SPA route changes don't trigger a full load, so pageviews need the
    // History API patch rather than the plain load-time capture.
    capture_pageview: "history_change",
    person_profiles: "identified_only",
  });

  // disableAnalytics() below persists an opt-out flag to localStorage
  // (key "__ph_opt_in_out_" + token) with no expiry, and posthog.init()
  // is a hard no-op once the SDK is already loaded (it returns early if
  // already loaded) — so a later Reject -> Accept-again round trip would
  // otherwise leave capture silently disabled forever even though our own
  // consent cookie says granted. Clear that stale flag on every successful
  // init. clear_opt_in_out_capturing() just removes the persisted flag
  // (consent.reset()); opt_in_capturing() is NOT used here because it has
  // side effects (clears persistence/session state, fires a capture event).
  posthog.clear_opt_in_out_capturing();
}

// Called when the user withdraws analytics consent via the preferences
// modal. posthog-js has no hard "uninit" — opt_out_capturing() is its
// documented mechanism to stop future capture.
export function disableAnalytics(): void {
  posthog.opt_out_capturing();
}
